import { TZDate } from '@date-fns/tz';
import { format, parse } from 'date-fns';
import { SHORT_DATE_OPTIONS } from './dateLocale';

export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

type DateInput = Date | string | number;

const supportedTimeZones = new Map<string, boolean>();

/**
 * `TZDate` and `Intl` both throw a RangeError on anything that is not an IANA zone name — an empty
 * string, an offset, or a Windows id such as "Pacific Standard Time". Probing is the only way to
 * ask, so the answer is memoised: the set of zones an app sees is tiny.
 */
export const isSupportedTimeZone = (timeZone: string): boolean => {
  const memoised = supportedTimeZones.get(timeZone);

  if (memoised !== undefined) return memoised;

  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format();
    supportedTimeZones.set(timeZone, true);

    return true;
  } catch {
    supportedTimeZones.set(timeZone, false);

    return false;
  }
};

/**
 * The same instant read through the center's clock. date-fns keeps the zone across every
 * operation applied to the result, so `format`, `startOfDay`, `isSameDay`, `addMinutes` … all
 * answer in center time rather than in whatever zone the guardian's device is set to.
 */
export const toCenterTime = (value: DateInput, timeZone: string): TZDate => new TZDate(new Date(value).getTime(), timeZone);

export const nowInCenterTime = (timeZone: string): TZDate => TZDate.tz(timeZone);

/** `TZDate.toISOString()` keeps the zone offset; API payloads carry the same instant as plain UTC. */
export const toUtcIsoString = (value: DateInput): string => new Date(Number(value)).toISOString();

export const formatInCenterTime = (value: DateInput, timeZone: string, pattern: string): string => format(toCenterTime(value, timeZone), pattern);

export const formatDateInCenterTime = (value: DateInput, timeZone: string, locale?: string): string =>
  new Date(value).toLocaleDateString(locale, { ...SHORT_DATE_OPTIONS, timeZone });

/**
 * Recurrence end dates are days, not instants, so they travel as a bare `yyyy-MM-dd` key and are
 * re-anchored at center midnight wherever a real date is needed.
 */
export const CENTER_DAY_KEY_FORMAT = 'yyyy-MM-dd';

export const toCenterDayKey = (value: DateInput, timeZone: string): string => formatInCenterTime(value, timeZone, CENTER_DAY_KEY_FORMAT);

export const centerDayFromKey = (dayKey: string, timeZone: string): TZDate => parse(dayKey, CENTER_DAY_KEY_FORMAT, new TZDate(0, timeZone));

/** Noon on a calendar day — the one instant of that day that no DST transition can make ambiguous. */
export const middayInTimeZone = (day: Date, timeZone: string): TZDate => new TZDate(day.getFullYear(), day.getMonth(), day.getDate(), 12, timeZone);

/** e.g. "PDT". date-fns' `zzz` token only renders a GMT offset, so the abbreviation comes from Intl. */
export const getTimeZoneAbbreviation = (value: DateInput, timeZone: string): string =>
  new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(new Date(value)).find((part) => part.type === 'timeZoneName')?.value ??
  '';
